import { _ } from "~/i18n";
import createSDSIcon from "~/ui/icon";
import PopOver, { IPopOverProps } from "~/ui/popover";
import { node, text } from "~/util/dom";
import { getLocation } from "~/whereami";
import Scene from "~/scene";
import { ROUTE_CONST } from "~/route/const";
import { getPOI } from "~/route/utils";
import { CLOSE, NOT_LISTED_LOCATION, ROUTE_FROM_HERE, ROUTE_HERE, REFRESH } from "~/ui/sds-compatible-icons";
import { DELETE_24 } from "~/util/sds-compatible-icons";
import { store } from "~/state/store";
import { IPoiData } from "~/wasm/interfaces";

interface IMapContextMenuProps extends Pick<IPopOverProps, "anchor" | "onClose" | "onOpen" | "position" | "clickOutIgnoredElements"> {
	longitude: number;
	latitude: number;
	pois: Array<IPoiData>;
	wasmStoreId: number;
}

export default class MapContextMenu {
	#popOver: PopOver;

	#content: HTMLMapyMapmenuElement;

	#dot: HTMLDivElement;

	constructor(props: IMapContextMenuProps) {
		const { anchor, onClose, onOpen, position, clickOutIgnoredElements, longitude, latitude, pois, wasmStoreId } = props;
		// FIXME: predelat getLocation na praci s longitude a latitude misto Scene a stejne tak getPOI
		const coords = Scene.coordsFromWGS84(longitude, latitude);

		/* eslint-disable-next-line */
		console.log("MapContextMenu", pois, wasmStoreId);

		this.#content = node("mapy-mapmenu");
		this.#dot = node("div", { className: "mapContextMenu__dot" });
		this.#popOver = new PopOver({
			anchor,
			onClose,
			onOpen,
			position,
			clickOutIgnoredElements,
			className: "mapContextMenu__popOver",
		});

		if (Mapy.getComponent("distanceMeter").isActive()) {
			const { geometries, markers } = store.getState().mapContent;
			const distanceMeterGeometry = geometries.find(geometry => geometry.id === wasmStoreId);
			const distanceMeterMarker = markers.find(marker => marker.id === wasmStoreId);

			if (distanceMeterGeometry && distanceMeterGeometry.type === "measure-line" && distanceMeterGeometry.customData?.index > -1) {
				// smazani itemu na danem indexu
				const distanceMeterRemoveItem = node("mapy-mapmenu-item", { className: "mapContextMenu__alertItem" });

				distanceMeterRemoveItem.append(createSDSIcon({ symbol: DELETE_24 }), text(_("distanceMeter.contextMenuItem.sector")));
				distanceMeterRemoveItem.addEventListener("click", () => {
					this.close();
					Mapy.getComponent("distanceMeter").getMain()?.remove(distanceMeterGeometry.customData?.index);
				});

				// slozime menu dohromady
				this.#content.append(
					distanceMeterRemoveItem,
				);
			} else if (distanceMeterMarker && (distanceMeterMarker.type === "measure-points" || distanceMeterMarker.type === "measure-start") && distanceMeterMarker.customData?.index > -1) {
				// smazani usermarku na danem indexu
				const distanceMeterRemoveItem = node("mapy-mapmenu-item", { className: "mapContextMenu__alertItem" });

				distanceMeterRemoveItem.append(createSDSIcon({ symbol: DELETE_24 }), text(_("distanceMeter.contextMenuItem.marker")));
				distanceMeterRemoveItem.addEventListener("click", () => {
					this.close();
					Mapy.getComponent("distanceMeter").getMain()?.remove(distanceMeterMarker.customData?.index);
				});

				// slozime menu dohromady
				this.#content.append(
					distanceMeterRemoveItem,
				);
			} else {
				// zruseni mereni
				const distanceMeterCancelItem = node("mapy-mapmenu-item");

				distanceMeterCancelItem.append(createSDSIcon({ symbol: CLOSE }), text(_("distanceMeter.cancel")));
				distanceMeterCancelItem.addEventListener("click", () => {
					this.close();
					Mapy.getComponent("distanceMeter").getMain()?.interactivePoint.hide(true);
				});

				// smazani vseho
				const distanceMeterRemoveAllItem = node("mapy-mapmenu-item");

				distanceMeterRemoveAllItem.append(createSDSIcon({ symbol: REFRESH }), text(_("distanceMeter.reload")));
				distanceMeterRemoveAllItem.addEventListener("click", () => {
					this.close();
					Mapy.getComponent("distanceMeter").getMain()?.removeAll();
				});

				// slozime menu dohromady
				this.#content.append(
					distanceMeterCancelItem,
					distanceMeterRemoveAllItem,
				);
			}
		} else if (Mapy.getComponent("userMarks").isActive()) {
			const { markers } = store.getState().mapContent;
			const userMarksMarker = markers.find(marker => marker.id === wasmStoreId);

			if (userMarksMarker?.type === "user-marks") {
				// smazani jednoho bodu
				const userMarksRemoveItem = node("mapy-mapmenu-item", { className: "mapContextMenu__alertItem" });

				userMarksRemoveItem.append(createSDSIcon({ symbol: DELETE_24 }), text(_("userMarks.deleteMark")));
				userMarksRemoveItem.addEventListener("click", () => {
					this.close();
					Mapy.getComponent("userMarks").getMain().remove(userMarksMarker.customData?.order - 1);
				});

				// slozime menu dohromady
				this.#content.append(
					userMarksRemoveItem,
				);
			} else {
				// smazani vseho
				const nomberOfUsermarks = Mapy.getComponent("userMarks").getMain().getItems().length;
				const userMarksRemoveAllItem = node("mapy-mapmenu-item", { className: "mapContextMenu__alertItem" });

				userMarksRemoveAllItem.append(createSDSIcon({ symbol: DELETE_24 }), text(_("userMarks.clear")));
				userMarksRemoveAllItem.addEventListener("click", () => {
					this.close();
					Mapy.getComponent("userMarks").getMain().removeAll();
					Mapy.getComponent("logger").log("points", {
						// eslint-disable-next-line camelcase
						end_points: "clear",
						count: nomberOfUsermarks,
					});
				});

				// slozime menu dohromady
				this.#content.append(
					userMarksRemoveAllItem,
				);
			}
		} else {
			const { markers } = store.getState().mapContent;
			const routeMarker = markers.find(marker => marker.id === wasmStoreId);

			if (routeMarker?.customData?.route) {
				// odebrat bod z trasy
				const routePointRemoveItem = node("mapy-mapmenu-item", { className: "mapContextMenu__alertItem" });

				routePointRemoveItem.append(createSDSIcon({ symbol: DELETE_24 }), text(_("route.form.contextItem.title")));
				routePointRemoveItem.addEventListener("click", () => {
					this.close();
					const items = [...Mapy.CML.state.route.items].filter((item, index) => index !== routeMarker.customData?.order - 1);

					Mapy.CML.state = {
						route: {
							items,
							scroll: true,
							zoom: false,
						},
					};
				});

				// slozime cast menu dohromady
				this.#content.append(
					routePointRemoveItem,
				);
			} else {
				const routeClick = (destination: "from" | "to") => {
					const routeComponent = Mapy.getComponent("route");

					getPOI(coords, true).then(data => {
						const routeState = Mapy.CML.state.route;
						const newRouteState = {
							route: {
								zoom: false,
								items: routeState?.items || [],
							},
						};

						switch (destination) {
							case "from":
								newRouteState.route.items.unshift({
									...data,
									position: ROUTE_CONST.position.start,
									favoriteId: "",
									usertitle: "",
								});
								break;

							case "to":
								newRouteState.route.items.push({
									...data,
									position: ROUTE_CONST.position.end,
									favoriteId: "",
									usertitle: "",
								});
								break;

							default:
								//
						}

						if (routeComponent.isActive()) {
							Mapy.CML.state = newRouteState;
							Mapy.getComponent("history").save();
						} else {
							routeComponent.activate().then(() => {
								Mapy.CML.state = newRouteState;
								Mapy.getComponent("history").save();
							}, () => {});
						}
					}, () => {});
				};

				// trasa odtud
				const routeFromHereItem = node("mapy-mapmenu-item");

				routeFromHereItem.append(createSDSIcon({ symbol: ROUTE_FROM_HERE }), text(_("route.contextRouteFrom")));
				routeFromHereItem.addEventListener("click", () => {
					this.close();
					routeClick("from");
				});

				// trasa sem
				const routeHereItem = node("mapy-mapmenu-item");

				routeHereItem.append(createSDSIcon({ symbol: ROUTE_HERE }), text(_("route.contextRouteTo")));
				routeHereItem.addEventListener("click", () => {
					this.close();
					routeClick("to");
				});

				// Co je zde?
				const whereAmIItem = node("mapy-mapmenu-item");

				whereAmIItem.append(createSDSIcon({ symbol: NOT_LISTED_LOCATION }), text(_("whereAmI")));
				whereAmIItem.addEventListener("click", () => {
					this.close();
					getLocation(coords).then(data => {
						Mapy.getComponent("detail").setState({
							source: data.source,
							id: data.id,
							coords: coords,
							gpsCoords: data.gpsCoords,
							manual: true,
							show: false,
						}).then(() => { Mapy.getComponent("history").save(); }, () => {});
					}, () => {
						Mapy.getComponent("detail").setState({
							source: "coor",
							id: `${longitude},${latitude}`,
							coords: coords,
							manual: true,
							show: false,
						}).then(() => { Mapy.getComponent("history").save(); }, () => {});
					});

					Mapy.getComponent("logger").log("menu-item", {
						type: "whereami",
						eventx: longitude.toFixed(6),
						eventy: latitude.toFixed(6),
					});
				}, false);

				// slozime zbytek menu dohromady
				this.#content.append(
					routeFromHereItem,
					routeHereItem,
					whereAmIItem,
				);
				this.#popOver.container.append(this.#dot);
			}
		}

		this.#popOver.container.append(this.#content);
	}

	destroy() {
		this.#popOver.destroy();
		this.#popOver = null;
	}

	get element() {
		return this.#content;
	}

	open() {
		this.#popOver.open();
		document.body.addEventListener("wheel", this, false);
	}

	close() {
		document.body.removeEventListener("wheel", this, false);
		this.#popOver?.close();
	}

	handleEvent(event: WheelEvent) {
		// pokud uzivatel scrolluje, ale ne tohle menu, zavreme menu
		if (event.type === "wheel" && !this.#popOver?.container?.contains(event.target as Node)) {
			this.close();
		}
	}
}
